import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { createMold, updateMold, checkDuplicateMoldNumber, getMolds } from '../services/mold.service';
import { customerService } from '../services/customer.service';
import { locationService } from '../services/location.service';
import { getProjects } from '../services/project.service';
import { QRService } from '../services/qr.service';
import storageService from '../services/storage.service';
import BarcodeDisplay from '../components/BarcodeDisplay';
import { Customer, Location, Mold, Project, CavityInfo } from '../types';

const MoldRegister: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [latestMoldNumber, setLatestMoldNumber] = useState('');

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [projects, setProjects] = useState<Project[]>([]); // ⭐ 프로젝트 목록

  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [file3D, setFile3D] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // 금형번호 중복 체크 상태
  const [moldNumberChecking, setMoldNumberChecking] = useState(false);
  const [moldNumberDuplicate, setMoldNumberDuplicate] = useState<boolean | null>(null);

  const [formData, setFormData] = useState({
    moldNumber: '',
    moldName: '',
    customerId: '',
    projectId: '',
    productName: '',
    cavityCount: 1,
    cavity: '',  // 캐비티 구성 (예: 1x8, 2x2)
    designLife: 100000,
    locationId: '',
    // v2.0 기초정보
    productNumber: '',
    productionStartDate: null as Date | null,
    disposalDate: null as Date | null,
    assetType: 'owned' as 'owned' | 'leased' | 'consigned',
    // v2.0 이력정보
    manufacturer: '',
    // v2.0 세부정보
    moldType: '' as '' | 'injection-trial' | 'injection-production' | 'press' | 'die-casting' | 'forging' | 'other',
    resinType: '',
    managerId: '',
    managerName: '',
    notes: '',
    // v2.0 보관정보
    weight: 0,
    dimensions: {
      width: 0,
      length: 0,
      height: 0,
    },
  });

  const [cavities, setCavities] = useState<CavityInfo[]>([]);

  const [qrPreview, setQrPreview] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [customerData, locationData, moldData, projectData] = await Promise.all([
          customerService.getAll(),
          locationService.getAll(),
          getMolds(),
          getProjects(),
        ]);
        setCustomers(customerData);
        setLocations(locationData);
        setProjects(projectData);
        
        // 최근 금형 조회
        if (moldData.length > 0) {
          const sorted = moldData.sort((a, b) => 
            (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
          );
          setLatestMoldNumber(sorted[0].moldNumber);
        }
      } catch (err) {
        console.error('참조 데이터를 불러오지 못했습니다:', err);
      }
    };
    loadData();
  }, []);

  // 초기 cavities 배열 생성 (마운트 시 1회)
  useEffect(() => {
    if (cavities.length === 0 && formData.cavityCount > 0) {
      const initialCavities = Array.from({ length: formData.cavityCount }, (_, i) => ({
        cavityNumber: i + 1,
        productName: '',
      }));
      setCavities(initialCavities);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 복제 데이터 처리
  useEffect(() => {
    const state = location.state as { duplicateData?: any };
    if (state?.duplicateData) {
      const data = state.duplicateData;
      setFormData({
        moldNumber: '', // 금형번호는 비워둠 (사용자가 새로 입력)
        moldName: data.moldName || '',
        customerId: data.customerId || '',
        projectId: data.projectId || '',
        productName: data.productName || '',
        cavityCount: data.cavityCount || 1,
        cavity: data.cavity || '',
        designLife: data.designLife || 100000,
        locationId: data.locationId || '',
        productNumber: data.productNumber || '',
        productionStartDate: data.productionStartDate || null,
        disposalDate: data.disposalDate || null,
        assetType: data.assetType || 'owned',
        manufacturer: data.manufacturer || '',
        moldType: data.moldType || '',
        resinType: data.resinType || '',
        managerId: data.managerId || '',
        managerName: data.managerName || '',
        notes: data.notes || '',
        weight: data.weight || 0,
        dimensions: data.dimensions || { width: 0, length: 0, height: 0 },
      });

      // cavities 데이터도 복사
      if (data.cavities && Array.isArray(data.cavities)) {
        setCavities(data.cavities);
      }
      
      // state 초기화 (뒤로가기 시 복제 데이터 재적용 방지)
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleInputChange =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const numericFields = ['cavityCount', 'designLife', 'weight'];
      const value = numericFields.includes(field)
        ? parseInt(event.target.value, 10) || 0
        : event.target.value;

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleDimensionChange =
    (dimension: 'width' | 'length' | 'height') => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(event.target.value, 10) || 0;
      setFormData((prev) => ({
        ...prev,
        dimensions: {
          ...prev.dimensions,
          [dimension]: value,
        },
      }));
    };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const totalImages = imageFiles.length + newFiles.length;

    if (totalImages > 4) {
      setError('이미지는 최대 4장까지 업로드할 수 있습니다.');
      return;
    }

    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    setImageFiles((prev) => [...prev, ...newFiles]);
  };

  const handleImageRemove = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handle3DFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtensions = [
      '3dm', '3ds', '3mf', 'amf', 'bim', 'brep', 
      'dae', 'fbx', 'fcstd', 'gltf', 'glb', 
      'ifc', 'iges', 'igs', 'step', 'stp', 
      'stl', 'obj', 'off', 'ply', 'wrl'
    ];
    const fileExt = file.name.split('.').pop()?.toLowerCase();

    if (!fileExt || !validExtensions.includes(fileExt)) {
      setError('지원하지 않는 3D 파일 형식입니다.');
      return;
    }

    setFile3D(file);
  };

  const handle3DFileRemove = () => {
    setFile3D(null);
  };

  // 패밀리 수 변경 시 cavities 배열 업데이트
  const handleCavityCountChange = (count: number) => {
    setFormData((prev) => ({ ...prev, cavityCount: count }));
    
    const newCavities = Array.from({ length: count }, (_, i) => ({
      cavityNumber: i + 1,
      productName: formData.productName || '',
    }));
    setCavities(newCavities);
  };

  // 개별 패밀리 제품명 변경 핸들러
  const handleCavityProductNameChange = (cavityNumber: number, productName: string) => {
    setCavities((prev) =>
      prev.map((c) => (c.cavityNumber === cavityNumber ? { ...c, productName } : c))
    );
  };

  useEffect(() => {
    const generateQRPreviewAsync = async () => {
      if (formData.moldNumber) {
        try {
          const tempMold = {
            id: `preview-${formData.moldNumber}`,
            moldNumber: formData.moldNumber,
          };

          const qrData = QRService.generateQRData(tempMold);
          const qrString = QRService.createQRString(qrData);
          const qrImage = await QRService.generateQRImage(qrString);
          setQrPreview(qrImage);
        } catch (err) {
          console.error('QR 코드를 생성하지 못했습니다:', err);
        }
      } else {
        setQrPreview(null);
      }
    };

    const timer = setTimeout(generateQRPreviewAsync, 500);
    return () => clearTimeout(timer);
  }, [formData.moldNumber]);

  // 금형번호 중복 체크 (실시간)
  useEffect(() => {
    const checkDuplicate = async () => {
      if (!formData.moldNumber || formData.moldNumber.length < 2) {
        setMoldNumberDuplicate(null);
        return;
      }

      setMoldNumberChecking(true);
      try {
        const isDuplicate = await checkDuplicateMoldNumber(formData.moldNumber);
        setMoldNumberDuplicate(isDuplicate);
      } catch (err) {
        console.error('중복 체크 실패:', err);
        setMoldNumberDuplicate(null);
      } finally {
        setMoldNumberChecking(false);
      }
    };

    const timer = setTimeout(checkDuplicate, 800);
    return () => clearTimeout(timer);
  }, [formData.moldNumber]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formData.moldNumber || !formData.moldName || !formData.customerId || !formData.locationId) {
      setError('필수 항목을 모두 입력해 주세요.');
      return;
    }

    // 이미 중복 체크된 경우
    if (moldNumberDuplicate === true) {
      setError(`금형번호 "${formData.moldNumber}"는 이미 사용 중입니다. 다른 번호를 입력해주세요.`);
      return;
    }

    setLoading(true);
    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // 최종 중복 체크 (실시간 체크와 제출 사이 시간 차이 대비)
      const isDuplicate = await checkDuplicateMoldNumber(formData.moldNumber);
      if (isDuplicate) {
        setError(`금형번호 "${formData.moldNumber}"는 이미 사용 중입니다. 다른 번호를 입력해주세요.`);
        setLoading(false);
        setIsUploading(false);
        setMoldNumberDuplicate(true);
        return;
      }

      const moldData: any = {
        moldNumber: formData.moldNumber,
        moldName: formData.moldName,
        customerId: formData.customerId,
        projectId: formData.projectId || null,
        productName: formData.productName,
        cavityCount: formData.cavityCount,
        designLife: formData.designLife,
        locationId: formData.locationId,
        currentShots: 0,
        status: 'stored',
        // 선택적 필드
        ...(formData.cavity && { cavity: formData.cavity }),
        ...(Array.isArray(cavities) && cavities.length > 0 && { cavities }),
        // v2.0 필드 (선택사항)
        ...(formData.productNumber && { productNumber: formData.productNumber }),
        ...(formData.productionStartDate && { productionStartDate: formData.productionStartDate }),
        ...(formData.disposalDate && { disposalDate: formData.disposalDate }),
        ...(formData.assetType && { assetType: formData.assetType }),
        ...(formData.manufacturer && { manufacturer: formData.manufacturer }),
        ...(formData.moldType && { moldType: formData.moldType }),
        ...(formData.resinType && { resinType: formData.resinType }),
        ...(formData.managerId && { managerId: formData.managerId }),
        ...(formData.managerName && { managerName: formData.managerName }),
        ...(formData.notes && { notes: formData.notes }),
        ...(formData.weight > 0 && { weight: formData.weight }),
        ...((formData.dimensions.width > 0 || formData.dimensions.length > 0 || formData.dimensions.height > 0) && { 
          dimensions: formData.dimensions 
        }),
      };

      const moldId = await createMold(moldData);

      if (!moldId) {
        throw new Error('금형을 생성하지 못했습니다.');
      }

      setUploadProgress(30);

      const imageUrls: string[] = [];
      const thumbnailUrls: string[] = [];
      if (imageFiles.length > 0) {
        for (let i = 0; i < imageFiles.length; i += 1) {
          const { original, thumbnail } = await storageService.uploadImageWithThumbnail(
            imageFiles[i], 
            moldId, 
            i
          );
          imageUrls.push(original);
          thumbnailUrls.push(thumbnail);
          setUploadProgress(30 + ((i + 1) / imageFiles.length) * 30);
        }
      }

      setUploadProgress(60);

      let file3DData: Mold['file3D'] = undefined;
      if (file3D) {
        file3DData = await storageService.upload3DFile(file3D, moldId);
        setUploadProgress(80);
      }

      if (imageUrls.length > 0 || file3DData) {
        const updateData: any = {
          images: imageUrls,
          thumbnails: thumbnailUrls,
        };
        
        // file3D가 있을 때만 추가
        if (file3DData) {
          updateData.file3D = file3DData;
        }
        
        await updateMold(moldId, updateData);
      }

      setUploadProgress(100);
      setSuccess(true);

      setTimeout(() => {
        navigate('/molds');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '등록하는 동안 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      setIsUploading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: { xs: 2, md: 3 },
        }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          금형이 성공적으로 등록되었습니다. 목록으로 이동합니다...
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/molds')}
          sx={{
            borderRadius: 2,
            px: 2.5,
            py: 1,
            fontWeight: 600,
            letterSpacing: '0.04em',
          }}
        >
          목록으로
        </Button>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            금형 등록
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.78) }}>
            금형 기본 정보와 파일을 업로드하면 QR 코드가 자동으로 생성됩니다.
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {location.state?.duplicateData && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          기존 금형 정보를 복제했습니다. 금형번호를 새로 입력한 후 등록해주세요.
        </Alert>
      )}

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
            theme.palette.primary.dark,
            0.26,
          )})`,
          boxShadow: `0 20px 38px ${alpha(theme.palette.primary.dark, 0.28)}`,
          backdropFilter: 'blur(14px)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: { xs: 2.5, md: 3 },
                }}
              >
                <TextField
                  required
                  fullWidth
                  label="금형 번호"
                  value={formData.moldNumber}
                  onChange={handleInputChange('moldNumber')}
                  placeholder={latestMoldNumber 
                    ? `예: M001 | 최근: ${latestMoldNumber}` 
                    : "예: M001"
                  }
                  error={moldNumberDuplicate === true}
                  helperText={
                    moldNumberChecking
                      ? '중복 확인 중...'
                      : moldNumberDuplicate === true
                      ? '이미 사용 중인 금형번호입니다.'
                      : moldNumberDuplicate === false
                      ? '사용 가능한 금형번호입니다.'
                      : ''
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: 'error.main',
                        },
                      },
                      ...(moldNumberDuplicate === false && {
                        '& fieldset': {
                          borderColor: 'success.main',
                        },
                      }),
                    },
                    '& .MuiFormHelperText-root': {
                      ...(moldNumberDuplicate === false && {
                        color: 'success.main',
                      }),
                    },
                  }}
                />

                <TextField
                  required
                  fullWidth
                  label="금형명"
                  value={formData.moldName}
                  onChange={handleInputChange('moldName')}
                  placeholder="예: 메인하우징 금형"
                />

                <TextField
                  required
                  fullWidth
                  select
                  label="고객사"
                  value={formData.customerId}
                  onChange={handleInputChange('customerId')}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  fullWidth
                  options={projects}
                  getOptionLabel={(option) => `${option.projectNo} - ${option.projectName}`}
                  value={projects.find(p => p.id === formData.projectId) || null}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({ ...prev, projectId: newValue?.id || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="연결 프로젝트 (선택사항)"
                      placeholder="프로젝트를 선택하세요"
                    />
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                    },
                  }}
                />

                <TextField
                  required
                  fullWidth
                  label="제품명"
                  value={formData.productName}
                  onChange={handleInputChange('productName')}
                  placeholder="예: 스위치 커버"
                />

                <TextField
                  fullWidth
                  label="캐비티 구성 (선택사항)"
                  value={formData.cavity}
                  onChange={handleInputChange('cavity')}
                  placeholder="예: 1x8, 2x2"
                  helperText="캐비티 배열을 입력하세요 (예: 1x8, 2x2)"
                />

                <TextField
                  required
                  fullWidth
                  label="패밀리 수"
                  type="number"
                  value={formData.cavityCount}
                  onChange={(e) => handleCavityCountChange(Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  helperText="캐비티(패밀리) 개수를 입력하세요"
                />

                <TextField
                  required
                  fullWidth
                  label="설계 수명 (샷)"
                  type="number"
                  value={formData.designLife}
                  onChange={handleInputChange('designLife')}
                  inputProps={{ min: 1000 }}
                />

                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                  <TextField
                    required
                    fullWidth
                    select
                    label="보관 위치"
                    value={formData.locationId}
                    onChange={handleInputChange('locationId')}
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name} ({location.zone})
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* 패밀리별 제품명 */}
                {cavities.length > 0 && (
                  <Box sx={{ gridColumn: { sm: 'span 2' }, mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      패밀리별 제품명
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                      {cavities.map((cavity) => (
                        <TextField
                          key={cavity.cavityNumber}
                          fullWidth
                          label={`패밀리 #${cavity.cavityNumber}`}
                          value={cavity.productName}
                          onChange={(e) => handleCavityProductNameChange(cavity.cavityNumber, e.target.value)}
                          placeholder="제품명을 입력하세요"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* v2.0 추가 정보 섹션 */}
                <Box sx={{ gridColumn: { sm: 'span 2' }, mt: 3, mb: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    추가 정보 (선택사항)
                  </Typography>
                </Box>

                {/* 기초정보 */}
                <TextField
                  fullWidth
                  label="제품 번호"
                  value={formData.productNumber}
                  onChange={handleInputChange('productNumber')}
                  placeholder="예: P-2024-001"
                />

                <TextField
                  fullWidth
                  select
                  label="자산 구분"
                  value={formData.assetType}
                  onChange={handleInputChange('assetType')}
                >
                  <MenuItem value="owned">자사소유</MenuItem>
                  <MenuItem value="leased">임대</MenuItem>
                  <MenuItem value="consigned">위탁</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  label="양산 시작일"
                  type="date"
                  value={formData.productionStartDate ? formData.productionStartDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, productionStartDate: e.target.value ? new Date(e.target.value) : null }))}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  label="폐기일"
                  type="date"
                  value={formData.disposalDate ? formData.disposalDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, disposalDate: e.target.value ? new Date(e.target.value) : null }))}
                  InputLabelProps={{ shrink: true }}
                />

                {/* 세부정보 */}
                <TextField
                  fullWidth
                  select
                  label="금형 종류"
                  value={formData.moldType}
                  onChange={handleInputChange('moldType')}
                >
                  <MenuItem value="">선택 안함</MenuItem>
                  <MenuItem value="injection-trial">사출금형(시금형)</MenuItem>
                  <MenuItem value="injection-production">사출금형(양산금형)</MenuItem>
                  <MenuItem value="press">프레스금형</MenuItem>
                  <MenuItem value="die-casting">다이캐스팅금형</MenuItem>
                  <MenuItem value="forging">단조금형</MenuItem>
                  <MenuItem value="other">기타</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  label="수지 정보"
                  value={formData.resinType}
                  onChange={handleInputChange('resinType')}
                  placeholder="예: PP, ABS, PC"
                />

                <TextField
                  fullWidth
                  label="금형 제작업체"
                  value={formData.manufacturer}
                  onChange={handleInputChange('manufacturer')}
                  placeholder="예: ABC 금형"
                />

                <TextField
                  fullWidth
                  label="관리 담당자 ID"
                  value={formData.managerId}
                  onChange={handleInputChange('managerId')}
                />

                <TextField
                  fullWidth
                  label="관리 담당자명"
                  value={formData.managerName}
                  onChange={handleInputChange('managerName')}
                />

                <TextField
                  fullWidth
                  label="중량 (kg)"
                  type="number"
                  value={formData.weight || ''}
                  onChange={handleInputChange('weight')}
                  inputProps={{ min: 0, step: 0.1 }}
                />

                {/* 치수 */}
                <TextField
                  fullWidth
                  label="가로 (mm)"
                  type="number"
                  value={formData.dimensions.width || ''}
                  onChange={handleDimensionChange('width')}
                  inputProps={{ min: 0 }}
                />

                <TextField
                  fullWidth
                  label="세로 (mm)"
                  type="number"
                  value={formData.dimensions.length || ''}
                  onChange={handleDimensionChange('length')}
                  inputProps={{ min: 0 }}
                />

                <TextField
                  fullWidth
                  label="높이 (mm)"
                  type="number"
                  value={formData.dimensions.height || ''}
                  onChange={handleDimensionChange('height')}
                  inputProps={{ min: 0 }}
                />

                {/* 비고 */}
                <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="비고"
                    value={formData.notes}
                    onChange={handleInputChange('notes')}
                    placeholder="특이사항이나 참고사항을 입력하세요"
                  />
                </Box>

                <Box sx={{ gridColumn: { sm: 'span 2' }, mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    <ImageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    이미지 업로드 (최대 4장)
                  </Typography>

                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    disabled={imageFiles.length >= 4 || isUploading}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      borderColor: alpha(theme.palette.primary.light, 0.38),
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: alpha(theme.palette.primary.light, 0.6),
                        backgroundColor: alpha(theme.palette.primary.light, 0.12),
                      },
                    }}
                  >
                    이미지 선택
                    <input type="file" hidden accept="image/*" multiple onChange={handleImageSelect} />
                  </Button>

                  {imagePreviews.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {imagePreviews.map((preview, index) => (
                        <Box
                          key={preview}
                          sx={{
                            position: 'relative',
                            width: 120,
                            height: 120,
                            border: `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.12)}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: alpha(theme.palette.primary.dark, 0.24),
                            boxShadow: `0 12px 24px ${alpha(theme.palette.primary.dark, 0.28)}`,
                          }}
                        >
                          <img
                            src={preview}
                            alt={`preview-${index}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleImageRemove(index)}
                            disabled={isUploading}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: alpha(theme.palette.common.black, 0.5),
                              color: theme.palette.common.white,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.common.black, 0.7),
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box sx={{ gridColumn: { sm: 'span 2' }, mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    <AttachFileIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    3D 파일 업로드 (STL, OBJ, STEP, IGES)
                  </Typography>

                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    disabled={!!file3D || isUploading}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      borderColor: alpha(theme.palette.primary.light, 0.38),
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: alpha(theme.palette.primary.light, 0.6),
                        backgroundColor: alpha(theme.palette.primary.light, 0.12),
                      },
                    }}
                  >
                    3D 파일 선택
                    <input
                      type="file"
                      hidden
                      accept=".3dm,.3ds,.3mf,.amf,.bim,.brep,.dae,.fbx,.fcstd,.gltf,.glb,.ifc,.iges,.igs,.step,.stp,.stl,.obj,.off,.ply,.wrl"
                      onChange={handle3DFileSelect}
                    />
                  </Button>

                  {file3D && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        border: `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.12)}`,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.dark, 0.2),
                      }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {file3D.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(file3D.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <IconButton onClick={handle3DFileRemove} disabled={isUploading} color="error">
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>

              {isUploading && (
                <Box>
                  <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                    업로드 중... {uploadProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}

              {qrPreview && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
                    backgroundColor: alpha(theme.palette.primary.dark, 0.18),
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    QR/바코드 미리보기
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 3,
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'center'
                  }}>
                    {/* QR 코드 */}
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        QR 코드
                      </Typography>
                      <img src={qrPreview} alt="QR Code Preview" style={{ width: 150, height: 150 }} />
                    </Box>

                    {/* 바코드 */}
                    {formData.moldNumber && (
                      <Box sx={{ textAlign: 'center', width: { xs: '100%', sm: 'auto' } }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          바코드
                        </Typography>
                        <Box sx={{ display: 'inline-block' }}>
                          <BarcodeDisplay
                            value={formData.moldNumber}
                            showLabel={false}
                            width={{ xs: 1.5, sm: 2 }}
                            height={{ xs: 40, sm: 50 }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    현재 입력한 정보를 기반으로 생성된 QR/바코드입니다.
                    <br />
                    최종 등록 시 자동으로 고유 ID가 포함됩니다.
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/molds')}
                  disabled={loading}
                  sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.primary.light, 0.38),
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.light, 0.6),
                      backgroundColor: alpha(theme.palette.primary.light, 0.12),
                    },
                  }}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ px: 3.5, py: 1.1, fontWeight: 600, letterSpacing: '0.06em' }}
                >
                  {loading ? <CircularProgress size={24} /> : '등록하기'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MoldRegister;
